import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { COMPANY, ENQUIRY_TYPES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_FORM = {
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    enquiry_type: "",
    message: "",
};

const inputCls =
    "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-navy-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-navy-900 focus:ring-2 focus:ring-navy-900/10";

const Field = ({ label, htmlFor, error, children, optional = false }) => (
    <div>
        <label
            htmlFor={htmlFor}
            className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600"
        >
            {label}
            {optional && <span className="ml-1 font-normal normal-case text-slate-400">(optional)</span>}
        </label>
        {children}
        {error && (
            <p data-testid={`${htmlFor}-error`} className="mt-1.5 text-xs font-medium text-red-700">
                {error}
            </p>
        )}
    </div>
);

export const Contact = ({ presetType }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");

    useEffect(() => {
        if (presetType) setForm((f) => ({ ...f, enquiry_type: presetType }));
    }, [presetType]);

    const update = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const validate = () => {
        const errs = {};
        if (form.full_name.trim().length < 2) errs.full_name = "Please enter your full name.";
        if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email address.";
        if (!form.enquiry_type) errs.enquiry_type = "Please select an enquiry type.";
        if (form.message.trim().length < 5) errs.message = "Please enter a short message.";
        return errs;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setStatus("sending");
        try {
            await axios.post(`${API}/enquiries`, form);
            setStatus("success");
            setForm(EMPTY_FORM);
            toast.success("Thank you — your enquiry has been sent to PISI LIMITED.");
        } catch {
            setStatus("error");
            toast.error("Your enquiry could not be sent. Please try again or contact us directly.");
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="bg-white">
            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
                <SectionHeading
                    eyebrow="Get In Touch"
                    title="Let's Talk About Your Requirements"
                    subtitle="Whether you are looking for everyday products, wholesale supply or further information about our business, get in touch with PISI LIMITED."
                />

                <div className="mt-14 grid gap-10 lg:grid-cols-5 lg:gap-0">
                    <Reveal className="lg:col-span-2">
                        <div
                            data-testid="contact-info-panel"
                            className="flex h-full flex-col bg-navy-900 p-8 lg:p-10"
                        >
                            <h3 className="font-heading text-lg font-semibold text-white">
                                Contact Information
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-300">
                                We aim to respond to all enquiries as quickly as possible.
                            </p>
                            <ul className="mt-9 space-y-7 text-sm">
                                <li className="flex items-start gap-4">
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-white/10 text-gold">
                                        <MapPin className="h-4 w-4" />
                                    </span>
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                                            Address
                                        </p>
                                        <address className="mt-1.5 not-italic leading-relaxed text-slate-200">
                                            <span className="font-semibold text-white">{COMPANY.name}</span>
                                            <br />
                                            {COMPANY.addressLines.map((line) => (
                                                <span key={line}>
                                                    {line}
                                                    <br />
                                                </span>
                                            ))}
                                        </address>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-white/10 text-gold">
                                        <Phone className="h-4 w-4" />
                                    </span>
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                                            Phone
                                        </p>
                                        <a
                                            href={COMPANY.phoneHref}
                                            data-testid="contact-phone-link"
                                            className="mt-1.5 block text-slate-200 transition-colors hover:text-white"
                                        >
                                            {COMPANY.phone}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-white/10 text-gold">
                                        <Mail className="h-4 w-4" />
                                    </span>
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                                            Email
                                        </p>
                                        <a
                                            href={COMPANY.emailHref}
                                            data-testid="contact-email-link"
                                            className="mt-1.5 block break-all text-slate-200 transition-colors hover:text-white"
                                        >
                                            {COMPANY.email}
                                        </a>
                                    </div>
                                </li>
                            </ul>
                            <div className="mt-auto hidden pt-10 lg:block">
                                <span className="block h-px w-full bg-white/10" />
                                <p className="mt-5 text-xs leading-relaxed text-slate-400">
                                    Wholesale &amp; retail supply — Croydon, United Kingdom
                                </p>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={120} className="lg:col-span-3">
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            noValidate
                            className="h-full border border-slate-200 bg-slate-50 p-8 lg:p-10"
                        >
                            <div className="grid gap-6 sm:grid-cols-2">
                                <Field label="Full Name" htmlFor="full_name" error={errors.full_name}>
                                    <input
                                        id="full_name"
                                        type="text"
                                        data-testid="contact-form-name-input"
                                        value={form.full_name}
                                        onChange={update("full_name")}
                                        placeholder="Your full name"
                                        className={inputCls}
                                        autoComplete="name"
                                    />
                                </Field>
                                <Field label="Business Name" htmlFor="business_name" optional error={errors.business_name}>
                                    <input
                                        id="business_name"
                                        type="text"
                                        data-testid="contact-form-business-input"
                                        value={form.business_name}
                                        onChange={update("business_name")}
                                        placeholder="Your business name"
                                        className={inputCls}
                                        autoComplete="organization"
                                    />
                                </Field>
                                <Field label="Email Address" htmlFor="email" error={errors.email}>
                                    <input
                                        id="email"
                                        type="email"
                                        data-testid="contact-form-email-input"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@example.com"
                                        className={inputCls}
                                        autoComplete="email"
                                    />
                                </Field>
                                <Field label="Phone Number" htmlFor="phone" optional error={errors.phone}>
                                    <input
                                        id="phone"
                                        type="tel"
                                        data-testid="contact-form-phone-input"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="Your phone number"
                                        className={inputCls}
                                        autoComplete="tel"
                                    />
                                </Field>
                                <div className="sm:col-span-2">
                                    <Field label="Enquiry Type" htmlFor="enquiry_type" error={errors.enquiry_type}>
                                        <select
                                            id="enquiry_type"
                                            data-testid="contact-form-enquiry-type-select"
                                            value={form.enquiry_type}
                                            onChange={update("enquiry_type")}
                                            className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23475569%22 stroke-width=%222%22%3E%3Cpath d=%22m6 9 6 6 6-6%22/%3E%3C/svg%3E')] bg-[position:right_1rem_center] bg-no-repeat pr-10 ${form.enquiry_type ? "" : "text-slate-400"}`}
                                        >
                                            <option value="" disabled>
                                                Select an enquiry type
                                            </option>
                                            {ENQUIRY_TYPES.map((type) => (
                                                <option key={type} value={type} className="text-navy-900">
                                                    {type}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                                <div className="sm:col-span-2">
                                    <Field label="Message" htmlFor="message" error={errors.message}>
                                        <textarea
                                            id="message"
                                            data-testid="contact-form-message-input"
                                            value={form.message}
                                            onChange={update("message")}
                                            placeholder="Tell us about the products or categories you are interested in..."
                                            rows={5}
                                            className={`${inputCls} resize-y`}
                                        />
                                    </Field>
                                </div>
                            </div>

                            {status === "success" && (
                                <div
                                    data-testid="contact-form-success-message"
                                    className="mt-6 flex items-start gap-3 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
                                    role="status"
                                >
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                                    Thank you for your enquiry. A member of our team will be in touch shortly.
                                </div>
                            )}
                            {status === "error" && (
                                <div
                                    data-testid="contact-form-error-message"
                                    className="mt-6 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                                    role="alert"
                                >
                                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                    Sorry, your enquiry could not be sent. Please try again, or contact us
                                    directly by phone or email.
                                </div>
                            )}

                            <button
                                type="submit"
                                data-testid="contact-form-submit-button"
                                disabled={status === "sending"}
                                className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-navy-900 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                            >
                                {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
                                {status === "sending" ? "Sending..." : "Send Enquiry"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
